import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { Box, IconButton, InputBase, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Cancel';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { CoachMessage } from '../CoachMessage';
import { MachineContext } from '../../MachineContext';
import { checkMessageObject, translateWithFallback } from '../../util';
import {EvoachButton} from "../Button";

export interface MultipleInputComponent extends BaseComponent {
  type: 'multipleInput';
  props: MultipleInputProps;
}
export interface MultipleInputProps {
  buttonText: string;
  saveResultTo: string;
  placeholderTexts: string[];
  presetValues?: string[];
  onChange?: (snippets: string[]) => void;
  showButton?: boolean;
  minItems?: number;
  maxItems?: number;
  toolTipCallback?: Function;
  warning?: string,
}

const getPlaceholderIndex = (
  index: number,
  placeholderTexts: string[]
): number => {
  return index < placeholderTexts.length ? index : placeholderTexts.length - 1;
};

export const MultipleInput: React.FC<MultipleInputProps> = ({
  buttonText,
  saveResultTo,
  placeholderTexts,
  presetValues,
  onChange,
  showButton = true,
  minItems,
  maxItems,
  toolTipCallback = () => {},
  warning = "", 
}) => {
  const { send } = useContext(SendContext);
  const [snippets, setSnippets] = useState<string[]>(
    presetValues ? presetValues : ['']
  );

  return (
    <div>
      {snippets.map((snippet, index) => {
        return (
          <Tooltip key={'_' + index + '__wrappingdiv_snippet'} title={warning == null ? "" : warning} onMouseEnter ={() =>{toolTipCallback(index, snippets.length)}}>
          <Box
            key={'_' + index + '__wrappingdiv_snippet'}
            sx={{
              paddingLeft: '10px',
              alignItems: 'center',
              justifyContent: 'space-between',
              display: 'flex',
              flexDirection: 'row',
              background: '#f2f2f2',
              color: 'rgba(0, 0, 0, 0.87)',
              transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '4px',
              boxShadow: '2px 2px 2px grey',
              margin: '0 0 8px 0',
            }}
          >
            <InputBase
              disabled={warning != null && warning != ""}
              placeholder={
                placeholderTexts[getPlaceholderIndex(index, placeholderTexts)]
              }
              value={snippet}
              key={index}
              onChange={(event) => {
                const value = event.target.value;
                const newSnippets = cloneDeep(snippets);
                newSnippets[index] = value;
                if (newSnippets[newSnippets.length - 1]) {
                  newSnippets.push('');
                }
                setSnippets(newSnippets);
                if (onChange) {
                  onChange(newSnippets);
                }
              }}
              sx={{
                marginLeft: '2px',
                height: '1em',
                width: '100%',
              }}
            />
            <IconButton
              onClick={() => {
                const newSnippets = cloneDeep(snippets);
                newSnippets.splice(index, 1);
                setSnippets(newSnippets);
                if (onChange) {
                  onChange(newSnippets);
                }
              }}
              size="large"
              disabled={snippets.length < 2}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          </Tooltip>
        );
      })}
      {showButton ? (
        <EvoachButton
          onClick={() => {
            const nonEmptySnippets = snippets.filter(
              (e: string) => e && e !== ''
            );
            send({
              type: 'writeToContext',
              payload: { [saveResultTo]: nonEmptySnippets },
            });
            send({
              type: 'next',
            });
          }}
          disabled={
            (minItems !== undefined &&
              minItems > snippets.filter((e) => e).length) ||
            (maxItems !== undefined &&
              maxItems < snippets.filter((e) => e).length)
          }
        >
          {buttonText}
        </EvoachButton>
      ) : (
        ''
      )}
    </div>
  );
};

/*
  ! Composite component that displays a CoachMessage before the snippet input
*/

export interface MultipleInputCompositeProps extends MultipleInputProps {
  message: string;
}
export interface MultipleInputCompositeComponent extends BaseComponent {
  type: 'multipleInputComposite';
  props: MultipleInputCompositeProps;
}

export const MultipleInputComposite: React.FC<MultipleInputCompositeProps> = ({
  buttonText,
  saveResultTo,
  placeholderTexts,
  presetValues,
  onChange,
  message,
  minItems,
  maxItems,
}) => {
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <CoachMessage message={message} />
      </div>
      <MultipleInput
        buttonText={buttonText}
        saveResultTo={saveResultTo}
        placeholderTexts={placeholderTexts}
        presetValues={presetValues}
        onChange={onChange}
        maxItems={maxItems}
        minItems={minItems}
      />
    </>
  );
};

export const renderMultipleInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const translatedplaceholdertexts = payload.placeholderTexts.map(
      (transkey: string) => {
        return translateWithFallback(intl, transkey);
      }
    );
    // if there is a variable, take it as value input and append an empty one
    // the empty one brings up an empty input field after the previous entries
    const content =
      payload.getValueFrom && payload.getValueFrom !== ''
        ? context.userData[payload.getValueFrom]
        : undefined;

    let presetValues = [];
    if (content) {
      // there is content in a variable
      if (typeof content === 'string') {
        presetValues.push(content);
      } else {
        if (Array.isArray(content)) {
          content.forEach((value: any) =>
            presetValues.push(checkMessageObject(value, 'value', intl))
          );
        }
      }
    }
    presetValues.push('');

    if (
      payload.message &&
      translateWithFallback(intl, payload.message).trim() !== ''
    ) {
      newContext.widgetData.push({
        type: 'multipleInputComposite',
        temporary: actionMetadata.action.temporary ?? false,
        props: {
          buttonText: translateWithFallback(intl, payload.buttonText),
          saveResultTo: payload.saveResultTo,
          placeholderTexts: translatedplaceholdertexts,
          presetValues: presetValues,
          message: translateWithFallback(intl, payload.message),
          maxItems: payload.maxItems,
          minItems: payload.minItems,
        },
      });
    } else {
      newContext.widgetData.push({
        type: 'multipleInput',
        temporary: actionMetadata.action.temporary ?? false,
        props: {
          buttonText: translateWithFallback(intl, payload.buttonText),
          saveResultTo: payload.saveResultTo,
          placeholderTexts: translatedplaceholdertexts,
          presetValues: presetValues,
          maxItems: payload.maxItems,
          minItems: payload.minItems,
        },
      });
    }
    return newContext;
  });
};
